import * as React from 'react'

import { Box, Icon, InputCheckBox, Table, Tag } from '@factorialco/deprecated-design-system'
import type { BookkeepersManagementIncidencePayrollIntegrationStatusQuery } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { dateBackendToUI } from 'modules/core/lib/dates'

import LastNameCell from 'modules/bookkeepers/components/TableComponents/LastNameCell'
import type { IncidenceQueryData } from 'modules/bookkeepers/hooks/useGetIncidences'
import useIncidenceTypeQueryParam from 'modules/bookkeepers/hooks/useIncidenceTypeQueryParam'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import {
  statusTranslation,
  translatedIncidenceName,
} from 'modules/bookkeepers/lib/dynamicTranslations'
import incidenceHasMessage from 'modules/bookkeepers/lib/hasNewMessage'
import { getIncidenceTypeNavigationMapping } from 'modules/bookkeepers/lib/incidenceTypeNavigation'
import tagColor from 'modules/bookkeepers/lib/tagColor'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'

import Cell from './Cell'
import PayrollIntegrationStatus from './PayrollIntegrationStatus'
import TaskStatuses from './TaskStatuses'

type BookkeepersManagementIncidencePayrollIntegrationStatus =
  BookkeepersManagementIncidencePayrollIntegrationStatusQuery['bookkeepersManagement']['incidencePayrollIntegrationStatusesConnection']['nodes'][number]

interface Props {
  isOpen: boolean
  isSelected: boolean
  isSelectable: boolean
  onToggleSelect?: (id: string) => void
  incidence: IncidenceQueryData
  payrollIntegrationStatus?: BookkeepersManagementIncidencePayrollIntegrationStatus
  showIncidenceTypeColumn: boolean
  showIncidencePayrollIntegrationStatus: boolean
  showCheckbox: boolean
}

const Item = ({
  isOpen,
  incidence,
  payrollIntegrationStatus,
  showIncidenceTypeColumn,
  showIncidencePayrollIntegrationStatus,
  isSelected,
  isSelectable,
  onToggleSelect,
  showCheckbox,
}: Props) => {
  const isBookkeeperView = useBookkeeperView()
  const { incidenceType } = useIncidenceTypeQueryParam()
  const hasMessageAdmin = incidenceHasMessage(isBookkeeperView, incidence)
  const mapping = getIncidenceTypeNavigationMapping()
  const filterTypeValuesFromParams = mapping[incidenceType]?.join(',')
  const filterTypesSource = filterTypeValuesFromParams
  const hasIncidenceFilteredByType = isBookkeeperView && !!filterTypesSource
  const { hasInhousePayroll } = useInhousePayroll()

  const checkboxSelected = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    onToggleSelect && onToggleSelect(incidence.id)
  }

  return (
    <>
      {showCheckbox && (
        <Table.Cell>
          <InputCheckBox checked={isSelected} disabled={!isSelectable} onClick={checkboxSelected} />
        </Table.Cell>
      )}
      <Table.Cell>
        <Box maxWidth={isBookkeeperView ? 's240' : undefined}>
          <Cell selected={isOpen} label={incidence.employee?.firstName} />
        </Box>
      </Table.Cell>

      <LastNameCell incidence={incidence} selected={isOpen} />
      {showIncidenceTypeColumn && (
        <Table.Cell>
          <Box maxWidth={isBookkeeperView ? 's240' : undefined}>
            <Cell
              selected={isOpen}
              label={translatedIncidenceName(incidence.name, incidence.customName)}
            />
          </Box>
        </Table.Cell>
      )}
      <Table.Cell>
        <Box maxWidth={isBookkeeperView ? 's240' : undefined}>
          <Cell selected={isOpen} label={incidence.legalEntity?.legalName ?? ''} />
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Box maxWidth={isBookkeeperView ? 's140' : undefined}>
          <Cell
            selected={isOpen}
            label={incidence.startsOn ? dateBackendToUI(incidence.startsOn) : ''}
          />
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Box alignItems="flexStart">
          <Tag
            ellipsis={false}
            type={tagColor(incidence.status, isBookkeeperView, hasInhousePayroll)}
          >
            {statusTranslation(incidence.status, isBookkeeperView, hasInhousePayroll)}
          </Tag>
        </Box>
      </Table.Cell>
      <PayrollIntegrationStatus
        payrollIntegrationStatus={payrollIntegrationStatus}
        showIncidencePayrollIntegrationStatus={showIncidencePayrollIntegrationStatus}
      />

      {hasIncidenceFilteredByType && <TaskStatuses taskStatuses={incidence.taskStatuses} />}
      {!isBookkeeperView && (
        <Table.Cell>
          {hasMessageAdmin && <Icon.Small icon="small-envelope" color="viridian800" />}
        </Table.Cell>
      )}
    </>
  )
}

export default observer(Item)
